import { HttpClient, HttpHeaders } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";

import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";

import { AlternativeCropSearchCriteria } from "@models/AlternativeCropSearchCriteria";
import { BasicSearchCriteria } from "@models/BasicSearchCriteria";
import { CopyrightContact } from "@models/CopyrightContact";
import { DurationSearchCriteria } from "@models/DurationSearchCriteria";
import { GroupSearchCriteria } from "@models/GroupSearchCriteria";
import { GrowthHabitSearchCriteria } from "@models/GrowthHabitSearchCriteria";
import { ImageSearchCriteria } from "@models/ImageSearchCriteria";
import { NoxiousInvasiveSearchCriteria } from "@models/NoxiousInvasiveSearchCriteria";
import { Plant } from "@models/Plant";
import { PlantCharacteristic } from "@models/PlantCharacteristic";
import { PlantDocumentation } from "@models/PlantDocumentation";
import { PlantEthnobotany } from "@models/PlantEthnobotany";
import { PlantInvasiveStatus } from "@models/PlantInvasiveStatus";
import { PlantLegalStatus } from "@models/PlantLegalStatus";
import { PlantLocation } from "@models/PlantLocation";
import { PlantNoxiousStatus } from "@models/PlantNoxiousStatus";
import { PlantPollinator } from "@models/PlantPollinator";
import { PlantRelatedLink } from "@models/PlantRelatedLink";
import { PlantWetlandData } from "@models/PlantWetlandData";
import { PlantWildlife } from "@models/PlantWildlife";
import { RaritySearchCriteria } from "@models/RaritySearchCriteria";
import { SearchCriteria } from "@models/SearchCriteria";
import { StateSearchCriteria } from "@models/StateSearchCriteria";
import { SubordinateTaxaResult } from "@models/SubordinateTaxaResult";
import { TopLevelSearchResult } from "@models/TopLevelSearchResult";
import { WetlandSearchCriteria } from "@models/WetlandSearchCriteria";

import { AppConfig } from "../app.config";

@Injectable({ providedIn: "root" })
export class PlantsService {
  private serviceUrl: string;
  private headers: HttpHeaders = new HttpHeaders({
    "Content-Type": "application/json"
  });
  private copyrightContact: CopyrightContact;

  public serviceCallFailed$ = new EventEmitter();

  plant: Plant;
  isLoading = new BehaviorSubject<boolean>(false);

  constructor(
    private appConfig: AppConfig,
    private http: HttpClient
  ) {
    this.serviceUrl = this.appConfig.getPlantsAPIUrl();
  }

  private async httpGet<T>(endpoint: string): Promise<T> {
    const url = this.serviceUrl + endpoint;
    try {
      return await firstValueFrom(this.http.get<T>(url, { headers: this.headers }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  private async httpPost<T>(endpoint: string, body: unknown): Promise<T> {
    const url = this.serviceUrl + endpoint;
    try {
      return await firstValueFrom(this.http.post<T>(url, body, { headers: this.headers }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  private async httpDownload(endpoint: string, body: unknown): Promise<Blob> {
    const BOM = "\uFEFF";
    const url = this.serviceUrl + endpoint;
    try {
      const response = await firstValueFrom(
        this.http.post(url, body, {
          headers: new HttpHeaders({
            "Content-Type": "application/json",
            Accept: "text/csv"
          }),
          responseType: "text"
        })
      );
      return new Blob([BOM + response], {
        type: "text/csv;charset=UTF-8"
      });
    } catch (error) {
      return this.handleError(error);
    }
  }

  setPlantProperty(property: Partial<Plant>): void {
    this.plant = { ...this.plant, ...property };
  }

  getPlants(): Promise<Plant[]> {
    return this.httpGet<Plant[]>("PlantList");
  }

  getPlant(id: number): Promise<Plant> {
    return this.httpGet<Plant>(`PlantProfile/${id.toString()}`);
  }

  getPlantBySymbol(symbol: string): Promise<Plant> {
    return this.httpGet<Plant>(`PlantProfile?symbol=${symbol}`);
  }

  getPlantBySymbolSearchName(symbol: string, searchName: string): Promise<Plant> {
    return this.httpGet<Plant>(`PlantProfile?symbol=${symbol}&searchName=${searchName}`);
  }

  getPlantByState(state: string): Promise<Plant> {
    return this.httpGet<Plant>(`RaritySearch?state=${state}`);
  }

  searchAhead(searchText: string): Observable<string[]> {
    if (searchText === "") {
      return of([]);
    }

    const url = this.serviceUrl + "PlantSearch?searchText=" + searchText;

    const results = this.http.get<string[]>(url, { headers: this.headers });

    return results;
  }

  getCharacteristics(id: number): Promise<PlantCharacteristic[]> {
    return this.httpGet<PlantCharacteristic[]>(`PlantCharacteristics/${id}`);
  }

  getSynonyms(id: number): Promise<Plant[]> {
    return this.httpGet<Plant[]>(`PlantSynonyms/${id}`);
  }

  getSubordinateTaxa(id: number, offset: number): Promise<SubordinateTaxaResult> {
    return this.httpGet<SubordinateTaxaResult>(`PlantSubordinateTaxa/${id}?offset=${offset}`);
  }

  getWildlife(id: number): Promise<PlantWildlife> {
    return this.httpGet<PlantWildlife>(`PlantWildlife/${id}`);
  }

  getWetlandData(id: number): Promise<PlantWetlandData[]> {
    return this.httpGet<PlantWetlandData[]>(`PlantWetland/${id}`);
  }

  getClassification(id: number): Promise<Plant> {
    return this.httpGet<Plant>(`Classification/${id}`);
  }

  getRelatedLinks(id: number): Promise<PlantRelatedLink[]> {
    return this.httpGet<PlantRelatedLink[]>(`PlantRelatedLinks/${id}`);
  }

  getEthnobotany(id: number): Promise<PlantEthnobotany[]> {
    return this.httpGet<PlantEthnobotany[]>(`PlantEthnobotany/${id}`);
  }

  getPollinator(id: number): Promise<PlantPollinator[]> {
    return this.httpGet<PlantPollinator[]>(`PlantPollinator/${id}`);
  }

  getLegalStatuses(id: number): Promise<PlantLegalStatus[]> {
    return this.httpGet<PlantLegalStatus[]>(`PlantLegalStatus/${id}`);
  }

  getAlternativeCropSearchCriteria(): Promise<AlternativeCropSearchCriteria> {
    return this.httpGet<AlternativeCropSearchCriteria>("AlternativeCropSearch");
  }

  getNoxiousStatuses(id: number): Promise<PlantNoxiousStatus[]> {
    return this.httpGet<PlantNoxiousStatus[]>(`PlantNoxiousStatus/${id}`);
  }

  getInvasiveStatuses(id: number): Promise<PlantInvasiveStatus[]> {
    return this.httpGet<PlantInvasiveStatus[]>(`PlantInvasiveStatus/${id}`);
  }

  getDocumentation(id: number, orderBy: string, offset: number): Promise<PlantDocumentation> {
    return this.httpGet<PlantDocumentation>(
      `PlantDocumentation/${id}?orderBy=${orderBy}&offset=${offset}`
    );
  }

  getImageSearchCriteria(
    previousCriteria: ImageSearchCriteria = null,
    newCriteria: ImageSearchCriteria = null
  ): Promise<ImageSearchCriteria> {
    return this.httpPost<ImageSearchCriteria>("ImageSearch/Criteria", {
      PreviousCriteria: previousCriteria,
      NewCriteria: newCriteria
    });
  }

  getStateSearchCriteria(): Promise<StateSearchCriteria> {
    return this.httpGet<StateSearchCriteria>("StateSearch");
  }

  getGroupSearchCriteria(): Promise<GroupSearchCriteria> {
    return this.httpGet<GroupSearchCriteria>("GroupSearch");
  }

  getRaritySearchCriteria(): Promise<RaritySearchCriteria> {
    return this.httpGet<RaritySearchCriteria>("RaritySearch");
  }

  getNoxiousInvasiveSearchCriteria(): Promise<NoxiousInvasiveSearchCriteria> {
    return this.httpGet<NoxiousInvasiveSearchCriteria>("NoxiousInvasiveSearch");
  }

  getPlantsWithCharacteristics(searchCriteria: SearchCriteria): Promise<TopLevelSearchResult> {
    return this.httpPost<TopLevelSearchResult>("CharacteristicsSearch", searchCriteria);
  }

  getDurationSearchCriteria(): Promise<DurationSearchCriteria> {
    return this.httpGet<DurationSearchCriteria>("DurationSearch");
  }

  getGrowthHabitSearchCriteria(): Promise<GrowthHabitSearchCriteria> {
    return this.httpGet<GrowthHabitSearchCriteria>("GrowthHabitSearch");
  }

  getWetlandSearchCriteria(): Promise<WetlandSearchCriteria> {
    return this.httpGet<WetlandSearchCriteria>("WetlandSearch");
  }

  getCountiesByState(stateId: number): Promise<PlantLocation[]> {
    return this.httpGet<PlantLocation[]>(`StateSearch/GetCounties/${stateId}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleError(error: any): Promise<any> {
    this.serviceCallFailed$.emit();
    console.error("An error occurred", error);
    return Promise.reject(error.message || error);
  }

  public storeCopyrightContact(copyrightContact: CopyrightContact) {
    this.copyrightContact = copyrightContact;
  }

  public getCopyrightContact() {
    return this.copyrightContact;
  }

  public getGSATStateList() {
    const url = this.serviceUrl + "plantsDownload/GetGSATStateList";
    return this.http.get(url);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getImageSearchResultFilter(filter: any): any {
    const url = this.serviceUrl + "imageSearch/getResultFilter";
    return this.http.post(url, filter);
  }

  public getPlantsGSATByState(state: string) {
    const url = this.serviceUrl + "plantsDownload/GetGSATByState?state=" + state;
    return this.http.get(url);
  }

  public getPlantUnkownSymbolCSV() {
    const url = this.serviceUrl + "plantsDownload/GetUnkownSymbolCSV";
    return this.http.get(url);
  }

  public getPlantDistributionData(searchCriteria: BasicSearchCriteria): Promise<Blob> {
    return this.httpDownload("PlantProfile/getDownloadDistributionDocumentation", searchCriteria);
  }

  public getCoverCropPlants(orderBy) {
    const url = this.serviceUrl + "common/getCoverCropPlants?orderBy=" + orderBy;
    return this.http.get(url);
  }

  public getCulturallySignificantPlants() {
    const url = this.serviceUrl + "common/getCulturallySignificantPlants";
    return this.http.get(url);
  }

  public getPlantsRarityByState(
    state: string,
    searchCriteria: RaritySearchCriteria
  ): Promise<TopLevelSearchResult> {
    return this.httpPost<TopLevelSearchResult>(
      `raritySearch/GetRarityByState?state=${state}`,
      searchCriteria
    );
  }

  public getPlantsNoxiousByState(
    state: string,
    searchCriteria: NoxiousInvasiveSearchCriteria
  ): Promise<TopLevelSearchResult> {
    return this.httpPost<TopLevelSearchResult>(
      `noxiousInvasiveSearch/GetNoxiousByState?state=${state}`,
      searchCriteria
    );
  }

  public getPlantsInvasiveByState(
    state: string,
    searchCriteria: NoxiousInvasiveSearchCriteria
  ): Promise<TopLevelSearchResult> {
    return this.httpPost<TopLevelSearchResult>(
      `noxiousInvasiveSearch/GetInvasiveByState?state=${state}`,
      searchCriteria
    );
  }
}
